import { dia, shapes } from 'jointjs'
import { Artifact, ArtifactType, Link, LinkType } from '@/models'
import { GraphLayout } from './graph-layout'
import { makeLink, makeArtifact, makeTypeContainer, makeLoadMorePill } from './rappid-helpers'
import ArtifactsCountPill from '../artifacts-count-pill'
import { useHelpers } from '../composables/helpers'
import { EventBus } from '@/event-bus'
import { useTraceDataStore } from '@/stores/traceDataStore'
import RappidContext from './rappid-context'

export default class GraphState {
  protected rappidContext: RappidContext

  artifacts: Map<number, dia.Cell> = new Map()
  artifactTypes: Map<number, dia.Cell> = new Map()

  links: Map<number, joint.dia.Link> = new Map()
  linkTypes: Map<number, joint.dia.Link> = new Map()

  public static readonly PROP_LINK = 'link'
  public static readonly PROP_LINK_TYPE = 'linkType'
  public static readonly PROP_ARTIFACT = 'artifact'
  public static readonly PROP_ARTIFACT_TYPE = 'artifactType'

  constructor(
    private graph: dia.Graph,
    private layouter: GraphLayout,
    ctx: RappidContext,
  ) {
    this.rappidContext = ctx
    graph.on('change:hidden', function (cell) {
      cell.attr('./opacity', cell.get('hidden') ? 0 : 1)
      cell.attr('./pointer-events', cell.get('hidden') ? 'none' : 1)
    })
    graph.on('change:collapsed', function () {
      layouter.layout()
    })
  }

  clear(): this {
    if (this.artifacts.size > 0) this.artifacts.clear()
    if (this.artifactTypes.size > 0) this.artifactTypes.clear()

    if (this.links.size > 0) this.links.clear()
    if (this.linkTypes.size > 0) this.linkTypes.clear()

    if (!this.graph.isEmpty()) this.graph.clear()

    return this
  }

  clearArtifactsAndLinks(): void {
    this.removeAllArtifactsFromModel()
    this.removeAllPills()
    this.removeAllLinksFromModel()
    this.collapseAllArtifactTypes()
    if (this.artifacts.size > 0) this.artifacts.clear()
    if (this.links.size > 0) this.links.clear()
  }

  removeAllArtifactsFromModel(): void {
    this.artifacts.forEach((artifact) => {
      this.graph.removeCells([artifact])
    })
  }

  removeAllLinksFromModel(): void {
    this.links.forEach((link) => {
      link.remove()
    })
  }

  collapseAllArtifactTypes(): void {
    this.artifactTypes.forEach((at) => {
      EventBus.emit('collapseContainer', at)
    })
    const traceDataStore = useTraceDataStore()
    traceDataStore.modelState.artifactTypes.forEach((at) => {
      at.collapsed = true
      at.artifactCount = 0
      at.loadedPages = 1
    })
  }

  removeArtifactsFromModel(artifactsToRemove: Artifact[]): this {
    artifactsToRemove.forEach((artifact) => {
      const currentArtifact = this.artifacts.get(artifact.id)
      if (currentArtifact !== undefined) {
        this.graph.removeCells([currentArtifact])
        this.artifacts.delete(artifact.id)
      }
    })

    return this
  }

  removeLinksFromModel(links: Link[]): this {
    for (const link of links) {
      const diaLink = this.links.get(link.id)
      if (diaLink !== undefined) {
        diaLink.remove()
      }

      this.links.delete(link.id)
    }

    return this
  }

  removeLinkTypesFromModel(linkTypes: LinkType[]): this {
    for (const linkType of linkTypes) {
      const link = this.linkTypes.get(linkType.id)
      if (link !== undefined) {
        link.remove()
      }

      this.linkTypes.delete(linkType.id)
    }

    return this
  }

  removeAllPills() {
    this.artifactTypes.forEach((at) => {
      let existingPill: dia.Cell<dia.Cell.Attributes, dia.ModelSetOptions> | undefined
      at.getEmbeddedCells().forEach((cell) => {
        if (cell && cell.get('isPill')) {
          existingPill = cell
        }
      })
      if (existingPill) {
        this.graph.removeCells([existingPill])
      }
    })
  }

  removePillFromModel(artifactType: ArtifactType): this {
    const pill = this.artifactTypes
      .get(artifactType.id)
      ?.getEmbeddedCells()
      .find((cell) => cell && cell.get('isPill'))

    if (pill) this.graph.removeCells([pill])

    return this
  }

  initializeOverview(artifactTypes: ArtifactType[], linkTypes: LinkType[]): this {
    let cells: dia.Cell[] = []

    artifactTypes
      // Only consider artifact types for which no JointJS element has been created yet
      .filter((at) => this.artifactTypes.get(at.id) === undefined)
      .forEach((at: ArtifactType) => {
        at.loadedPages = 0

        const typeContainer = makeTypeContainer(at)

        typeContainer.set('collapsed', true)
        typeContainer.attr('search/visibility', 'hidden')

        this.artifactTypes.set(at.id, typeContainer)

        cells.push(typeContainer)
      })

    cells = cells.concat(this.addLinkTypesToModel(linkTypes))

    this.graph.addCells(cells)

    this.addArtifactsCountPillToTypeContainer()

    return this
  }

  addToModel({
    artifacts,
    artifactTypes,
    links,
    linkTypes,
  }: {
    artifacts: Artifact[]
    artifactTypes: ArtifactType[]
    links: Link[]
    linkTypes?: LinkType[]
  }): this {
    // Add artifacts and artifact types
    this.graph.addCells(this.addArtifactsToModel(artifacts, artifactTypes, links, false))

    // Add link types
    this.graph.addCells(this.addLinkTypesToModel(linkTypes ?? []))

    // Add links last, some link-type links might be replace by regular links
    this.graph.addCells(this.addLinksToModel(links, artifacts))

    this.addArtifactsCountPillToTypeContainer()

    return this
  }

  protected addArtifactsToModel(
    artifacts: Artifact[],
    artifactTypes: ArtifactType[],
    links: Link[],
    searchMode: boolean,
  ): dia.Cell[] {
    const cells = []

    function groupArtifactsByType(
      artifacts: Artifact[],
      artifactTypes: ArtifactType[],
    ): Map<number, Artifact[]> {
      const artifactsMap = new Map<number, Artifact[]>()

      for (const artifactType of artifactTypes) {
        artifactsMap.set(artifactType.id, [])
      }

      for (const artifact of artifacts) {
        if (artifactsMap.has(artifact.artifactType.id)) {
          artifactsMap.get(artifact.artifactType.id)?.push(artifact)
        } else {
          artifactsMap.set(artifact.artifactType.id, [artifact])
        }
      }

      return artifactsMap
    }

    for (const [artifactTypeId, group] of groupArtifactsByType(artifacts, artifactTypes)) {
      const artifactType = artifactTypes.find((a) => a.id === artifactTypeId)
      if (!artifactType) continue
      if (searchMode) {
        artifactType.artifactCount = group.length
      }

      const maxArtifactsInContainer = artifactType.maxArtifactCountToShow ?? 4

      let typeContainer = this.artifactTypes.get(artifactType.id)

      const numberOfEmbeddedArtifacts =
        typeContainer?.getEmbeddedCells().filter((cell) => cell.get('isPill') === undefined)
          .length ?? 0

      if (numberOfEmbeddedArtifacts === 0 || numberOfEmbeddedArtifacts < maxArtifactsInContainer) {
        group
          // Sort artifacts group so that artifacts with the most links are first
          .sort((a, b) => {
            const { getNumberOfLinksForArtifact } = useHelpers()

            const linksCountA = getNumberOfLinksForArtifact(a, links)
            const linksCountB = getNumberOfLinksForArtifact(b, links)

            return (
              (linksCountB || Number.MIN_SAFE_INTEGER) - (linksCountA || Number.MIN_SAFE_INTEGER)
            )
          })
          // Only consider artifacts for which no JointJS element has been created yet
          .filter((a) => !this.artifacts.has(a.id))
          // Slice the grouped artifacts to show only the first ones
          .slice(0, maxArtifactsInContainer - numberOfEmbeddedArtifacts)
          .forEach((artifact) => {
            if (!typeContainer) {
              typeContainer = makeTypeContainer(artifactType)

              this.artifactTypes.set(artifactType.id, typeContainer)
              cells.push(typeContainer)
            } else {
              if (searchMode) {
                typeContainer.attributes.collapsed = false

                EventBus.emit('expandContainer', typeContainer)
                const traceDataStore = useTraceDataStore()
                traceDataStore.toggleArtifactTypeCollapsed(artifactType, false)
              }
            }

            const artifactCell = makeArtifact(artifact)
            typeContainer.embed(artifactCell)

            typeContainer.attributes.collapsed = artifactType.collapsed
            if (!typeContainer.attributes.collapsed) {
              typeContainer.attr('search/visibility', 'visible')
            }

            if (typeContainer?.attributes.collapsed) {
              artifactCell.set('hidden', true)
              artifactCell.attr('./opacity', 0)
              artifactCell.attr('./pointer-events', 'none')
            }

            this.artifacts.set(artifact.id, artifactCell)
            cells.push(artifactCell)
          })
      }

      if (typeContainer) {
        // Special case for no search results
        if (artifactType.containerSearchArtifactCount === 0) {
          ;(typeContainer as any).resize(typeContainer.attributes.original_width, 150)
        }

        const totalArtifactsCount =
          artifactType.containerSearchArtifactCount ?? artifactType.artifactCount ?? group.length

        const hiddenArtifactsCount = totalArtifactsCount - maxArtifactsInContainer

        const pill = this.addLoadMorePillToContainer(
          artifactType,
          typeContainer,
          totalArtifactsCount,
          maxArtifactsInContainer,
          hiddenArtifactsCount,
          searchMode,
        )

        if (pill) cells.push(pill)
      }
    }

    return cells
  }

  protected addLinkTypesToModel(linkTypes: LinkType[]): dia.Cell[] {
    return (
      linkTypes
        .filter((lt) => ![...this.links].some(([, l]) => l.attributes.link.linkType.id === lt.id))
        // Only consider link types for which no JointJS element has been created yet
        .filter((lt) => this.linkTypes.get(lt.id) === undefined)
        // Only consider link types for which both source and target JointJS element are present
        .filter(
          (lt) =>
            this.artifactTypes.has(lt.participantA?.id as number) &&
            this.artifactTypes.has(lt.participantB?.id as number),
        )
        .map((lt) => {
          const linkSource = this.artifactTypes.get(lt.participantA?.id as number) as joint.dia.Cell
          const linkTarget = this.artifactTypes.get(lt.participantB?.id as number) as joint.dia.Cell

          const link = makeLink(lt, linkSource, linkTarget)

          this.linkTypes.set(lt.id, link)

          return link
        })
    )
  }

  filterModel({
    artifacts,
    artifactTypes,
    links,
    linkTypes,
  }: {
    artifacts: Artifact[]
    artifactTypes: ArtifactType[]
    links: Link[]
    linkTypes?: LinkType[]
  }): this {
    this.clearArtifactsAndLinks()

    this.graph.addCells(this.addArtifactsToModel(artifacts, artifactTypes, links, true))

    this.graph.addCells(this.addLinkTypesToModel(linkTypes ?? []))

    // Add links last, some link-type links might be replace by regular links
    this.graph.addCells(this.addLinksToModel(links, artifacts))

    const { getArtifactTypeFromCellView } = useHelpers()

    this.graph.getCells().forEach((element) => {
      const elementView = element.findView(this.rappidContext.paper)
      elementView.removeTools()
      const currentAT = getArtifactTypeFromCellView(elementView)
      if (currentAT !== undefined && !elementView.model.get('isPill')) {
        const artifactsCountPill = new ArtifactsCountPill(elementView, currentAT)

        const toolsView = new dia.ToolsView({
          tools: [artifactsCountPill],
        })
        elementView.addTools(toolsView)
        elementView.showTools()
      }
    })

    return this
  }

  protected addLinksToModel(links: Link[], artifacts: Artifact[]): dia.Cell[] {
    return links
      .map((newLink) => {
        // The new link is going to replace all link type elements
        this.removeLinkTypesFromModel([newLink.linkType])

        const artifactA = artifacts.find((a) => a.id === newLink.artifactAId)
        const artifactB = artifacts.find((a) => a.id === newLink.artifactBId)

        if (!artifactA || !artifactB) return

        const artifactACell = this.artifacts.get(newLink.artifactAId)
        const artifactBCell = this.artifacts.get(newLink.artifactBId)

        const artifactATypeCell = this.artifactTypes.get(artifactA.artifactType.id)
        const artifactBTypeCell = this.artifactTypes.get(artifactB.artifactType.id)

        // If no artifact cell is present for the link, have it connect to the corresponding artifact type.
        // In the future when the artifact is loaded, this link needs to be updated.
        const linkSource = artifactACell ?? artifactATypeCell
        const linkTarget = artifactBCell ?? artifactBTypeCell

        if (!linkSource || !linkTarget) return

        let link = this.links.get(newLink.id)

        if (link === undefined) {
          link = makeLink(newLink, linkSource, linkTarget)
        } else {
          // Link has been created earlier when may not have been available yet, let's reset its source and target.
          link.source(linkSource)
          link.target(linkTarget)
          link.attr('original_source', undefined)
          link.attr('original_target', undefined)
        }

        // If the artifact is hidden meaning the artifact type is collapsed,
        // assign source and/or target to be the artifact type cell.
        if (linkSource.attributes.hidden && artifactATypeCell) {
          link.set('original_source', linkSource)
          link.source(artifactATypeCell)
        }

        if (linkTarget.attributes.hidden && artifactBTypeCell) {
          link.set('original_target', linkTarget)
          link.target(artifactBTypeCell)
        }

        this.links.set(newLink.id, link)

        return link
      })
      .filter((l): l is shapes.standard.Link => l !== undefined)
  }

  layout(): void {
    this.layouter.layout()
  }

  protected addLoadMorePillToContainer(
    artifactType: ArtifactType,
    container: dia.Cell,
    totalNumberOfArtifacts: number,
    loadedArtifactsCount: number,
    hiddenArtifactsCount: number,
    searchMode: boolean,
  ): shapes.standard.Rectangle | undefined {
    this.removePillFromModel(artifactType)

    // If there is no artifacts left to load, just return undefined after removing the old pill
    if (hiddenArtifactsCount <= 0) {
      return undefined
    }

    // create bubble with hidden artifact count:
    const newPill = makeLoadMorePill(
      artifactType,
      container,
      totalNumberOfArtifacts,
      loadedArtifactsCount,
      searchMode,
    )
    container.embed(newPill)

    return newPill
  }

  protected addArtifactsCountPillToTypeContainer(): void {
    const { getArtifactTypeFromCellView } = useHelpers()

    this.graph.getCells().forEach((element) => {
      const elementView = element.findView(this.rappidContext.paper)
      const currentAT = getArtifactTypeFromCellView(elementView)

      if (!element.get('isPill') && currentAT !== undefined) {
        const artifactsCountPill = new ArtifactsCountPill(elementView, currentAT)

        const toolsView = new dia.ToolsView({
          tools: [artifactsCountPill],
        })

        elementView.addTools(toolsView)
        elementView.showTools()
      }
    })
  }
}
